import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [exploreDropdown, setExploreDropdown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-gradient-to-r from-white to-gray-100 shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-[1330px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Rounded pill container */}
          <div className={`rounded-full px-12 py-6 shadow-lg transition-all duration-300 ${
            isScrolled 
              ? 'bg-white border border-gray-200 mt-2 mb-2' 
              : 'bg-white/10 backdrop-blur-md border border-white/20 mt-4'
          }`}>
            <div className="flex justify-between items-center">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img 
                  src="/logooo.svg" 
                  alt="iSolve RCM" 
                  className="w-auto h-[60px] object-contain"
                />
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-2">
                <Link to="/rcm-services" className="px-5 py-2 rounded-full text-lg font-medium transition-all text-gray-800 hover:bg-gray-100">
                  RCM Services
                </Link>
                <Link to="/specialties" className="px-5 py-2 rounded-full text-lg font-medium transition-all text-gray-800 hover:bg-gray-100">
                  Specialities
                </Link>
                
                {/* Explore Dropdown */}
                <div 
                  className="relative"
                  onMouseEnter={() => setExploreDropdown(true)}
                  onMouseLeave={() => setExploreDropdown(false)}
                >
                  <button className="px-5 py-2 rounded-full text-lg font-medium transition-all text-gray-800 hover:bg-gray-100 flex items-center">
                    Explore
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {exploreDropdown && (
                    <div className="absolute top-full left-0 pt-2 w-48 z-50">
                      <div className="bg-white rounded-xl shadow-xl border border-gray-200 py-2">
                        <Link to="/about-us" className="block px-4 py-2 text-gray-800 hover:bg-gray-100 text-lg">
                          About Us
                        </Link>
                        <Link to="/pricing" className="block px-4 py-2 text-gray-800 hover:bg-gray-100 text-lg">
                          Pricing
                        </Link>
                        <Link to="/privacy-policy" className="block px-4 py-2 text-gray-800 hover:bg-gray-100 text-lg">
                          Privacy Policy
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                <Link to="/contact-us" className="px-5 py-2 rounded-full text-lg font-medium transition-all text-gray-800 hover:bg-gray-100">
                  Contact Us
                </Link>
              </nav>

              {/* Mobile menu button */}
              <div className="lg:hidden">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-2 rounded-full focus:outline-none transition-all text-gray-800 hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Menu */}
      <>
        {/* Overlay */}
        <div 
          className={`fixed inset-0 bg-black/50 z-[60] lg:hidden transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setIsMenuOpen(false)}
        ></div>

        {/* Sidebar */}
        <div className={`fixed left-0 top-0 h-full w-80 bg-white z-[70] shadow-2xl lg:hidden transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="p-6 h-full overflow-y-auto">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src="/logooo.svg" 
                alt="iSolve RCM" 
                className="h-16 w-auto"
              />
            </div>

            {/* Close button */}
            <button
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-4 right-4 p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Menu Items */}
            <nav className="mt-4 space-y-2">
              <Link 
                to="/" 
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-gray-800 hover:bg-gray-100 rounded-lg text-lg font-medium transition-all"
              >
                Home
              </Link>
              
              <Link 
                to="/about-us" 
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-gray-800 hover:bg-gray-100 rounded-lg text-lg font-medium transition-all"
              >
                About Us
              </Link>
              
              <Link 
                to="/rcm-services" 
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-gray-800 hover:bg-gray-100 rounded-lg text-lg font-medium transition-all"
              >
                Services
              </Link>
              
              <Link 
                to="/pricing" 
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-gray-800 hover:bg-gray-100 rounded-lg text-lg font-medium transition-all"
              >
                Pricing
              </Link>
              
              <Link 
                to="/specialties" 
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-gray-800 hover:bg-gray-100 rounded-lg text-lg font-medium transition-all"
              >
                Specialties
              </Link>
              
              <Link 
                to="/contact-us" 
                onClick={() => setIsMenuOpen(false)}
                className="block px-4 py-3 text-gray-800 hover:bg-gray-100 rounded-lg text-lg font-medium transition-all"
              >
                Contact
              </Link>
            </nav>
          </div>
        </div>
      </>
    </>
  );
};

export default Header;
